import type { Item, Level, Vec2 } from '../core/hp/types';
import { itemFootprint, type Footprints } from '../core/geometry/footprint';
import { transformItemWithStack, type SurfaceProbe } from '../core/geometry/stacking';
import { exceedsDragThreshold } from './planGesture';

export type SceneGestureKind = 'move' | 'rotate' | 'resize' | 'height' | 'camera';
export class ScenePointerIntent {
  active = false;
  constructor(readonly pointerId: number, readonly x: number, readonly y: number, readonly kind: SceneGestureKind) {}
  update(x: number, y: number): boolean {
    this.active ||= this.kind === 'move' ? exceedsDragThreshold(x - this.x, y - this.y)
      : Math.hypot(x - this.x, y - this.y) >= (this.kind === 'rotate' || this.kind === 'height' ? 1 : 6);
    return this.active;
  }
}

/** Below the file's 0.01° and above the parser's 1e-5 rad rounding: a yaw closer than this to another, or a whole turn away, is the same. */
const YAW_EPSILON = 1e-4;
export const sameYaw = (a: number, b: number) => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b))) < YAW_EPSILON;

export interface Point3 { x: number; y: number; z: number }
export function rayPlanePoint(origin: Point3, direction: Point3, y: number, reach = Infinity): Point3 | null {
  if (Math.abs(direction.y) < 1e-5) return null;
  const t = (y - origin.y) / direction.y;
  if (t <= 0 || !Number.isFinite(t)) return null;
  const distance = Math.min(t, reach);
  return { x: origin.x + direction.x * distance, y: origin.y + direction.y * distance, z: origin.z + direction.z * distance };
}

export function containsPoint(polygon: readonly Vec2[], point: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j], b = polygon[i], dx = b.x - a.x, dz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.z - a.z) * dz) / (dx * dx + dz * dz || 1)));
    if (Math.hypot(point.x - a.x - dx * t, point.z - a.z - dz * t) < 1e-7) return true;
    if ((a.z > point.z) !== (b.z > point.z) && point.x < (b.x - a.x) * (point.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

export function constrainItemToRoom(item: Item, polygon: Vec2[], footprints?: Footprints, previous = item.position): Vec2 {
  const constrained = { ...item, position: { ...item.position } };
  for (let pass = 0; pass < 32; pass++) {
    let moved = false;
    for (let index = 0; index < 4; index++) {
      const corner = itemFootprint(constrained, footprints)[index];
      if (containsPoint(polygon, corner)) continue;
      let closest = corner, distance = Infinity;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i], b = polygon[(i + 1) % polygon.length], dx = b.x - a.x, dz = b.z - a.z;
        const t = Math.max(0, Math.min(1, ((corner.x - a.x) * dx + (corner.z - a.z) * dz) / (dx * dx + dz * dz || 1)));
        const point = { x: a.x + dx * t, z: a.z + dz * t }, d = Math.hypot(point.x - corner.x, point.z - corner.z);
        if (d < distance) { closest = point; distance = d; }
      }
      constrained.position.x += closest.x - corner.x; constrained.position.z += closest.z - corner.z;
      moved = true;
    }
    if (!moved) break;
  }
  return itemFootprint(constrained, footprints).every(point => containsPoint(polygon, point)) ? constrained.position : { ...previous };
}

export type SceneItemTransform = Pick<Item, 'position' | 'rotation'> & Partial<Pick<Item, 'elevation' | 'wallId'>>;
export function previewSceneItemTransform(base: Level, id: string, transform: SceneItemTransform, probe?: SurfaceProbe): Level {
  const source = base.items.find(item => item.id === id);
  if (!source) return base;
  if (source.position.x === transform.position.x && source.position.z === transform.position.z && source.rotation === transform.rotation
    && (transform.elevation === undefined || transform.elevation === source.elevation) && (transform.wallId === undefined || transform.wallId === source.wallId)) return base;
  return transformItemWithStack(base, id, transform, transform.wallId === undefined ? undefined : { wallId: transform.wallId }, probe);
}

export function snapSceneRotation(rotation: number, disabled = false): number {
  const snapped = Math.round(rotation / (Math.PI / 12)) * (Math.PI / 12);
  return !disabled && Math.abs(rotation - snapped) < Math.PI / 60 ? snapped : rotation;
}
